import { render } from "preact";
import { AfterCall, PersonCard } from "./person";
import { useEffect } from "preact/hooks";
import { startBridge } from "../embed/host";
import { loadSiteConfig } from "./config";
import { t } from "./i18n";
import { Icon } from "./icon";
import { CallsPanel } from "./pages/calls";
import { Contacts } from "./pages/contacts";
import { Diagnostics } from "./pages/diagnostics";
import { dialerKey, Dialer } from "./pages/dialer";
import { History } from "./pages/history";
import { Messages } from "./pages/messages";
import { Voicemail } from "./pages/voicemail";
import { installPrompt, Settings } from "./pages/settings";
import { Setup } from "./pages/setup";
import * as S from "./state";
import "./styles.css";

function Nav({ page, icon, label, badge }: { page: S.Page; icon: string; label: string; badge?: number }) {
  const current = S.page.value === page || (page === "settings" && S.page.value === "diagnostics");
  return (
    <button
      type="button"
      class="nav"
      aria-current={current ? "page" : undefined}
      onClick={() => {
        S.page.value = page;
        if (page === "history") S.missed.value = 0;
      }}
    >
      <Icon name={icon} size={22} />
      <span>{label}</span>
      {!!badge && <span class="badge">{badge}</span>}
    </button>
  );
}

function Rail() {
  const reg = S.registration.value;
  const dnd = S.prefs.value.dnd;
  return (
    <nav class="rail" aria-label="MocTel Vox">
      <span class="mark" title="MocTel Vox">
        <Icon name="app-mark" size={40} />
      </span>
      <Nav page="dialer" icon="keypad" label={t("nav.keypad")} />
      <Nav page="contacts" icon="contacts" label={t("nav.contacts")} />
      {S.chatEnabled.value && <Nav page="messages" icon="chat" label={t("nav.messages")} badge={S.chatUnread.value} />}
      <Nav page="history" icon="history" label={t("nav.history")} badge={S.missed.value} />
      {S.vmAvailable.value && (
        <Nav page="voicemail" icon="voicemail" label={t("nav.voicemail")} badge={S.vmMessages.value.filter((m) => !m.read).length} />
      )}
      <span class="spacer" />
      <Nav page="settings" icon="settings" label={t("nav.settings")} />
      <div class="line-status" data-testid="line-status" data-state={reg}>
        <span class={`dot ${reg === "registered" ? "ok" : reg === "failed" ? "bad" : reg === "none" ? "" : "wait"}`} />
        <span style={{ color: dnd ? "var(--railWarning)" : undefined }}>{dnd ? t("status.dnd") : reg === "registered" ? t("status.online") : t("status.offline")}</span>
      </div>
    </nav>
  );
}

function Toast() {
  const v = S.toast.value;
  useEffect(() => {
    if (!v) return;
    const i = setTimeout(() => {
      if (S.toast.value?.n === v.n) S.toast.value = null;
    }, 4000);
    return () => clearTimeout(i);
  }, [v?.n]);
  return v ? (
    <div class="toast" role="status">
      {v.text}
    </div>
  ) : null;
}

function NotifyBanner() {
  // framed: the host page owns notifications (its call.state events)
  if (S.embedded) return null;
  if (typeof Notification === "undefined" || Notification.permission !== "default" || S.prefs.value.notifications === false) return null;
  return (
    <div class="banner">
      <span>{t("notify.allow")}</span>
      <span class="row">
        <button type="button" class="btn primary" onClick={() => void S.askNotifications()}>
          {t("notify.enable")}
        </button>
        <button type="button" class="btn link" aria-label={t("common.close")} onClick={() => void S.setPrefs({ notifications: false })}>
          <Icon name="close" size={16} />
        </button>
      </span>
    </div>
  );
}

function App() {
  useEffect(() => {
    // keys nobody used: digits go to the open in-call keypad as DTMF, else
    // to the dialer when it is shown (the desktop app's rule)
    const onKey = (e: KeyboardEvent) => {
      if (S.setupNeeded.value || e.ctrlKey || e.metaKey || e.altKey) return;
      const tgt = e.target as HTMLElement;
      if (tgt.closest("input, select, textarea, [contenteditable]")) return;
      if (S.dtmfCall.value && /^[0-9*#]$/.test(e.key)) {
        void S.dtmf(S.dtmfCall.value, e.key);
        e.preventDefault();
        return;
      }
      if (S.page.value === "dialer" && dialerKey(e)) e.preventDefault();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);

  if (!S.ready.value) return <div class="setup" aria-busy="true" />;
  if (S.setupNeeded.value) {
    return (
      <>
        <Setup />
        <Toast />
      </>
    );
  }
  const pages = { dialer: Dialer, contacts: Contacts, messages: Messages, history: History, voicemail: Voicemail, settings: Settings, diagnostics: Diagnostics };
  const Page = pages[S.page.value];
  return (
    <div class="shell">
      <Rail />
      <main class="main">
        <NotifyBanner />
        <Page />
      </main>
      {S.calls.value.length > 0 ? <CallsPanel /> : <AfterCall />}
      <PersonCard />
      <Toast />
    </div>
  );
}

addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  installPrompt.value = e as unknown as { prompt(): Promise<void> };
});

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  void navigator.serviceWorker.register("/sw.js");
  navigator.serviceWorker.addEventListener("message", (e) => S.onServiceWorkerMessage(e.data));
}

if (S.embedded) document.documentElement.dataset.embedded = "";
render(<App />, document.getElementById("app")!);
void loadSiteConfig().then(async (cfg) => {
  await S.boot(cfg);
  if (S.embedded) startBridge(cfg.embed_origins);
});
